/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useId } from "react"

import type { Organization } from "../../features/organization/organization-types"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../primitives/card"
import { Field, FieldLabel } from "../primitives/field"
import { NativeSelect, NativeSelectOption } from "../primitives/native-select"

interface Props {
    /**
     * Organizations the admin can pick from.
     */
    organizations: Organization[]
    /**
     * Called with the picked organization. The view loads its users.
     */
    onOrganizationSelected: (orgId: number) => void
}

/**
 * Asks an admin with more than one organization to pick the one whose users to list.
 *
 * Picking an organization applies it at once, so the select always shows its
 * placeholder.
 */
export const AllUsersSelectOrgView = observer(({ organizations, onOrganizationSelected }: Props) => {
    const selectId = useId()

    return (
        <div className="w-content">
            <Card className="max-w-md">
                <CardHeader>
                    <CardTitle>
                        <h1 className="m-0 text-lg leading-normal font-semibold text-primary">
                            Select an organization
                        </h1>
                    </CardTitle>
                    <CardDescription>To continue, select an organization from the list below.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Field>
                        <FieldLabel htmlFor={selectId}>Organization</FieldLabel>
                        <NativeSelect
                            id={selectId}
                            className="w-full"
                            value=""
                            onChange={(e) => onOrganizationSelected(Number(e.target.value))}
                        >
                            <NativeSelectOption value="" disabled>
                                Select organization...
                            </NativeSelectOption>
                            {organizations.map((org) => (
                                <NativeSelectOption key={org.id} value={org.id}>
                                    {org.name}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </Field>
                </CardContent>
            </Card>
        </div>
    )
})
